import type { ProcedureStep, Stored, User } from "@overmap-ai/common"
import type { ReactNode } from "react"

import {
	UserArrayEqualsFilterModifier,
	UserArrayEveryFilterModifier,
	UserArrayNoneFilterModifier,
	UserArrayNotEqualsFilterModifier,
	UserArraySomeFilterModifier,
} from "../../modifiers"
import { BaseFilterDef, createFilterModifierConfig, type FilterDefRenderProps } from "../BaseFilterDef"
import { ProcedureStepReviewerFilterCell } from "./ProcedureStepReviewerFilterCell"

type ProcedureStepReviewerFieldValue = User["id"][]
type ProcedureStepReviewerFilterValue = User["id"][]

export class ProcedureStepReviewerFilterDef extends BaseFilterDef<
	ProcedureStepReviewerFieldValue,
	ProcedureStepReviewerFilterValue
> {
	readonly id: string
	readonly defaultFilterValue: ProcedureStepReviewerFilterValue | undefined = undefined
	readonly defaultFilterModifier: keyof typeof this.modifiers = "equals"

	readonly procedureStep: Stored<ProcedureStep>
	readonly users: User[]

	constructor(procedureStep: Stored<ProcedureStep>, users: User[]) {
		super()
		this.procedureStep = procedureStep
		this.users = users
		this.id = `procedure-step-reviewers:${procedureStep.uuid}`
	}

	readonly modifiers = {
		equals: createFilterModifierConfig({
			modifier: UserArrayEqualsFilterModifier,
			isValueValid: (value: ProcedureStepReviewerFieldValue): value is User["id"][] => Array.isArray(value),
			isFilterValueValid: (filterValue: ProcedureStepReviewerFilterValue): filterValue is User["id"][] =>
				Array.isArray(filterValue),
		}),
		notEquals: createFilterModifierConfig({
			modifier: UserArrayNotEqualsFilterModifier,
			isValueValid: (value: ProcedureStepReviewerFieldValue): value is User["id"][] => Array.isArray(value),
			isFilterValueValid: (filterValue: ProcedureStepReviewerFilterValue): filterValue is User["id"][] =>
				Array.isArray(filterValue),
		}),
		every: createFilterModifierConfig({
			modifier: UserArrayEveryFilterModifier,
			isValueValid: (value: ProcedureStepReviewerFieldValue): value is User["id"][] => Array.isArray(value),
			isFilterValueValid: (filterValue: ProcedureStepReviewerFilterValue): filterValue is User["id"][] =>
				Array.isArray(filterValue),
		}),
		some: createFilterModifierConfig({
			modifier: UserArraySomeFilterModifier,
			isValueValid: (value: ProcedureStepReviewerFieldValue): value is User["id"][] => Array.isArray(value),
			isFilterValueValid: (filterValue: ProcedureStepReviewerFilterValue): filterValue is User["id"][] =>
				Array.isArray(filterValue),
		}),
		none: createFilterModifierConfig({
			modifier: UserArrayNoneFilterModifier,
			isValueValid: (value: ProcedureStepReviewerFieldValue): value is User["id"][] => Array.isArray(value),
			isFilterValueValid: (filterValue: ProcedureStepReviewerFilterValue): filterValue is User["id"][] =>
				Array.isArray(filterValue),
		}),
	}

	render(props: FilterDefRenderProps<ProcedureStepReviewerFilterDef>): ReactNode {
		return <ProcedureStepReviewerFilterCell key={props.filter.id} {...props} />
	}
}
